import React, { useEffect, useState } from 'react';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {
  ALMOST_WHITE_COLOR, BACKGROUND_DARK,
  BLACK_COLOR,
  BORDER_DARK, DODGER_BLUE, EXPAND_ROW_COLOR_DARK, FRESHDESK_GREY, LIGHT_GREY_COLOR,
  PRIMARY_COLOR, VERY_LIGHT_GREY_COLOR, WHITE_COLOR,
} from '../../../const/color-const';
import {
  fetchContact,
  fetchTickets,
  fetchConversationById, FRESHDESK_STATUSES,
} from '../../../actions/freshdesk-actions';
import { FormattedMessage } from 'react-intl';
import { editorThemeStyles } from './NewTicket';
import PropTypes from 'prop-types';
import CommonContainer from '../../common/CommonContainer';
import CircularProgress from '@material-ui/core/CircularProgress';
import 'react-quill/dist/quill.core.css';
import { darken } from '@material-ui/core/styles';
import CommonSnackbar from '../../common/CommonSnackbar';
import TicketList from './TicketsList';
import { TicketListHeader } from './TicketHeaderSection';

const BORDER_COLOR_DARK = '#3d3d46';
const TICKET_SHADOW_LIGHT = '0px 0px 20px -20px rgba(0,0,0,0.09),0px 25px 20px -20px rgba(0,0,0,0.12)';
const TICKET_SHADOW_DARK = '0px 0px 20px -20px rgba(0,0,0,0.09),0px 25px 20px -20px rgba(0,0,0,0.32)';

const useStyles = makeStyles(theme => ({
  freshdeskHeader: {
    paddingTop: theme.spacing(3),
    position: 'relative',
    textAlign: 'center',
  },
  closedHeader: {
    marginTop: theme.spacing(12),
  },
  tasksWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    alignSelf: 'center',
    borderRadius: '8px',
    width: '100%',
    marginBottom: theme.spacing(12),
  },
  taskContent: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    borderRadius: 10,
    maxWidth: '940px',
    justifyContent: 'center',
    margin: theme.spacing(1, 2, 2, 2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1, 1, 0, 1),
    },
  },
  taskStatusWrapper: {
    padding: 0,
    fontSize: 14,
    color: 'gray',
    width: '100%',
    display: 'flex',
    fontWeight: 500,
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    margin: theme.spacing(0, 1, 0, 1),
    [theme.breakpoints.up('md')]: {
      margin: theme.spacing(0.2, 9.5, 1, 9.5),
    },
    '& p': {
      margin: 0,
    },
  },
  dotsSection: {
    fontSize: 36,
    display: 'flex',
    userSelect: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    color: LIGHT_GREY_COLOR,
    padding: theme.spacing(2, 2, 4, 2),
  },
  headerWrapper: {
    width: '100%',
    display: 'flex',
    cursor: 'pointer',
    borderTop: theme.palette.type === 'light' ? `1px dotted ${LIGHT_GREY_COLOR}` : `1px solid ${BORDER_COLOR_DARK}`,
    borderRight: theme.palette.type === 'light' ? `1px dotted ${LIGHT_GREY_COLOR}` : `1px solid ${BORDER_COLOR_DARK}`,
    borderLeft: theme.palette.type === 'light' ? `1px dotted ${LIGHT_GREY_COLOR}` : `1px solid ${BORDER_COLOR_DARK}`,
    borderBottom: theme.palette.type === 'light' ? `1px solid ${VERY_LIGHT_GREY_COLOR}` : `unset`,
    boxShadow: theme.palette.type === 'light' ? TICKET_SHADOW_LIGHT : TICKET_SHADOW_DARK,
    borderTopRightRadius: 10,
    borderTopLeftRadius: 10,
    alignItems: 'center',
    textAlign: 'center',
    position: 'relative',
    padding: theme.spacing(1),
    justifyContent: 'space-between',
    height: 64,
    [theme.breakpoints.up('md')]: {
      height: 60,
    },
    background: theme.palette.type === 'light' ? WHITE_COLOR : `${darken(BACKGROUND_DARK, 0.3)}`,
    [theme.breakpoints.up('md')]: {
      margin: theme.spacing(0, 8, 0, 8),
    },
  },
  headerWrapperExpanded: {
    background: theme.palette.type === 'light' ? `${FRESHDESK_GREY}` : `${darken(BACKGROUND_DARK, 0.3)}`,
    lineHeight: 'inherit',
  },
  headerWrapperSupport: {
    background: PRIMARY_COLOR,
    '& span, p': {
      color: 'black',
    },
    '& svg': {
      fill: DODGER_BLUE,
    },
  },
  headerIcon: {
    display: 'flex',
    transformOrigin: 'center',
    width: '100%',
    maxWidth: '21%',
    [theme.breakpoints.up('md')]: {
      maxWidth: '30%',
    },
    '& svg': {
      transformOrigin: 'center',
      transition: 'all 0.4s ease',
      color: theme.palette.type === 'light' ? DODGER_BLUE : ALMOST_WHITE_COLOR,
      height: 32,
      width: 32,
      [theme.breakpoints.up('md')]: {
        height: 38,
        width: 38,
      },
    },
  },
  headerIconExpanded: {
    transformOrigin: 'center',
    '& svg': {
      transformOrigin: 'center',
      transform: 'rotate(-180deg)',
    },
  },
  headerItem: {
    width: '100%',
    fontWeight: 600,
    maxWidth: '50%',
    margin: 0,
    padding: 0,
    lineHeight: '18px',
    [theme.breakpoints.up('md')]: {
      lineHeight: 'unset',
      maxWidth: '30%',
    },
  },
  headerDate: {
    width: '100%',
    fontWeight: 600,
    margin: 0,
    padding: 0,
    textAlign: 'right',
    maxWidth: '21%',
    [theme.breakpoints.up('md')]: {
      maxWidth: '30%',
    },
  },
  task: {
    width: '100%',
    marginLeft: 'auto',
    borderBottomRightRadius: 10,
    borderBottomLeftRadius: 10,
    border: theme.palette.type === 'light' ? '1px solid lightgrey' : `1px solid ${BORDER_COLOR_DARK}`,
    boxShadow: theme.palette.type === 'light' ? `0 0 6px 0 ${FRESHDESK_GREY}` : `0 0 20px 0 ${BORDER_DARK}`,
    [theme.breakpoints.up('md')]: {
      margin: theme.spacing(0, 8, 0.5, 8),
    },
  },
  description: {
    textAlign: 'center',
    margin: theme.spacing(2, 2, 7, 2),
  },
  ticketWrapper: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    borderBottomRightRadius: 10,
    borderBottomLeftRadius: 10,
  },
  commentWrapper: {
    width: '100%',
    position: 'relative',
    top: 0,
    [theme.breakpoints.up('md')]: {
      margin: theme.spacing(0, 8, 0.5, 8),
    },
  },
  commentContent: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    borderRadius: 10,
    margin: theme.spacing(2, 0, 2, 0),
    border: theme.palette.type === 'light' ? '1px solid lightgrey' : `1px solid ${BORDER_COLOR_DARK}`,
    boxShadow: theme.palette.type === 'light' ? `0 0 6px 0 ${FRESHDESK_GREY}` : `0 0 20px 0 ${BORDER_DARK}`,
  },
  commentDetails: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    color: 'black',
    padding: theme.spacing(0, 1, 0, 1),
    background: PRIMARY_COLOR,
    boxShadow: theme.palette.type === 'light' ? 'none' : 'inset 5px 5px 15px 5px rgba(0,0,0,0.25)',
    borderBottom: theme.palette.type === 'light' ? '1px solid lightgrey' : `1px solid ${BORDER_COLOR_DARK}`,
    position: 'relative',
  },
  ticketName: {
    maxWidth: '48%',
    width: '100%',
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  ticketTimestamp: {
    maxWidth: '50%',
    width: '100%',
    textAlign: 'right',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  ticketSubject: {
    fontWeight: '600',
  },
  ticketHeader: {
    height: 50,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1.5, 1, 1.5),
    borderTop: theme.palette.type === 'light' ? `1px dotted ${LIGHT_GREY_COLOR}` : `2px dotted ${BLACK_COLOR}`,
    borderRight: theme.palette.type === 'light' ? `1px dotted ${LIGHT_GREY_COLOR}` : `2px dotted ${BLACK_COLOR}`,
    borderLeft: theme.palette.type === 'light' ? `1px dotted ${LIGHT_GREY_COLOR}` : `2px dotted ${BLACK_COLOR}`,
  },
  ticketContent: {
    width: '100%',
    minHeight: 58,
    display: 'flex',
    borderRadius: 10,
    alignItems: 'flex-start',
    flexDirection: 'column',
    justifyContent: 'center',
    background: theme.palette.type === 'light' ? WHITE_COLOR : `${EXPAND_ROW_COLOR_DARK}`,
    ...editorThemeStyles(theme),
    '& .ql-editor': {
      minHeight: 100,
    },
    '& .ql-container > .ql-editor': {
      minHeight: 100,
    },
    '& > div': {
      wordBreak: 'break-word',
    },
  },
  buttonsWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    borderTop: theme.palette.type === 'light' ? `1px solid ${VERY_LIGHT_GREY_COLOR}` : `1px solid ${BORDER_COLOR_DARK}`,
    background: theme.palette.type === 'light' ? WHITE_COLOR : `${EXPAND_ROW_COLOR_DARK}`,
  },
  customButton: {
    margin: theme.spacing(1),
    color: theme.palette.type === 'light' ? BLACK_COLOR : ALMOST_WHITE_COLOR,
    backgroundColor: theme.palette.type === 'light' ? ALMOST_WHITE_COLOR : EXPAND_ROW_COLOR_DARK,
    '&:hover': {
      background: theme.palette.type === 'light' ? darken(ALMOST_WHITE_COLOR, 0.03) : darken(EXPAND_ROW_COLOR_DARK, 0.2),
    },
  },
  closeButton: {
    margin: theme.spacing(1),
  },
  ticketResponse: {
    background: theme.palette.type === 'light' ? WHITE_COLOR : `${EXPAND_ROW_COLOR_DARK}`,
    padding: theme.spacing(2, 1, 2, 1),
    borderRadius: 10,
    minHeight: 50,
    '& img': {
      maxWidth: 780,
    },
  },
  newReplySection: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    margin: theme.spacing(2, 0, 2, 0),
    position: 'relative',
    top: 0,
    '& > div': {
      borderRadius: 10,
    },
  },
  addCommentReplyBtn: {
    color: theme.palette.type === 'light' ? BLACK_COLOR : ALMOST_WHITE_COLOR,
    backgroundColor: theme.palette.type === 'light' ? VERY_LIGHT_GREY_COLOR : EXPAND_ROW_COLOR_DARK,
    [theme.breakpoints.up('md')]: {
      margin: theme.spacing(2, 8, 4, 8),
    },
  },
  ticketDetails: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 1, 0.5, 1),
    borderTopRightRadius: 10,
    borderTopLeftRadius: 10,
    borderBottom: theme.palette.type === 'light' ? `1px solid lightgrey` : `1px solid ${BORDER_DARK}`,
    background: theme.palette.type === 'light' ? `${FRESHDESK_GREY}` : `${darken(BACKGROUND_DARK, 0.3)}`,
    color: theme.palette.type === 'light' ? 'black' : 'white',
    position: 'relative',
    fontWeight: 500,
    width: '100%',
    height: 50,
  },
  customIcon: {
    borderRadius: 25,
    width: 40,
    height: 40,
    background: PRIMARY_COLOR,
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  supportIcon: {
    position: 'absolute',
    fontSize: 18,
    left: -50,
  },
  replyIcon: {
    background: theme.palette.type === 'light' ? `${FRESHDESK_GREY}` : `${EXPAND_ROW_COLOR_DARK}`,
    border: theme.palette.type === 'light' ? '1px solid lightgrey' : `1px solid ${BORDER_COLOR_DARK}`,
    color: theme.palette.type === 'light' ? BLACK_COLOR : ALMOST_WHITE_COLOR,
    position: 'absolute',
    fontWeight: 400,
    fontSize: 18,
    right: -50,
  },
}));

const FreshdeskPage = ({
                         freshdeskTickets,
                         conversations,
                         fetchTickets,
                         fetchContact,
                         fetchConversationById,
                         user,
                       }) => {
  const [isLoading, setLoading] = useState(true);
  const [openTickets, setOpenTickets] = useState([]);
  const [fetchedConv, setFetchedConv] = useState(new Set());
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const classes = useStyles();
  const { EMail } = user;
  
  useEffect(() => {
    fetchContact(EMail);
  }, [EMail, fetchContact]);
  
  useEffect(() => {
    if (!freshdeskTickets.length) {
      fetchTickets(EMail, setLoading);
    } else {
      setLoading(false);
      if (freshdeskTickets.length > 0 && openTickets.length === 0) {
        for (const ticket of freshdeskTickets) {
          if (ticket.status === FRESHDESK_STATUSES.OPEN.code) {
            setOpenTickets([ticket.id]);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [EMail, freshdeskTickets.length, fetchTickets]);
  
  useEffect(() => {
    const openTicket = openTickets[openTickets.length - 1];
    if (openTicket && !fetchedConv.has(openTicket) && !conversations.some(conv => conv.ticketId === openTicket)) {
      fetchConversationById(openTicket);
      setFetchedConv(prevSet => new Set(prevSet).add(openTicket));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTickets, fetchConversationById, conversations]);
  
  if (isLoading) {
    return (
      <CommonContainer vAlign="center">
        <CircularProgress />
      </CommonContainer>
    );
  }
  
  const handleOpenTickets = (ticketId) => {
    setOpenTickets(currentOpenTickets => {
      if (currentOpenTickets.includes(ticketId)) {
        return currentOpenTickets.filter(id => id !== ticketId);
      } else {
        return [...currentOpenTickets, ticketId];
      }
    });
  };
  
  const openTicketsList = freshdeskTickets.filter((ticket) => ticket.status === FRESHDESK_STATUSES.OPEN.code);
  
  const closedTicketsList = freshdeskTickets.filter((ticket) => ticket.status === FRESHDESK_STATUSES.CLOSED.code);
  
  return (
    <div className={classes.tasksWrapper}>
      
      <TicketListHeader
        title={<FormattedMessage id="freshdesk.tickets" defaultMessage="My tickets" />}
        description={<FormattedMessage id="freshdesk.description"
                                       defaultMessage="Here you can <b>read</b>, <b>edit</b> or <b>reply</b> to your <b>open</b> tickets."
                                       values={{ b: (chunks) => <strong>{chunks}</strong> }}
        />}
        freshdeskTickets={openTicketsList}
        classes={classes}
      />
      
      <TicketList
        tickets={openTicketsList}
        handleOpenTickets={handleOpenTickets}
        openTickets={openTickets}
        classes={classes}
        setIsSnackbarOpen={setIsSnackbarOpen}
      />
      
      {closedTicketsList.length !== 0 && <TicketListHeader
        title={<FormattedMessage id="freshdesk.header.closed" defaultMessage="Closed Tickets" />}
        description={<FormattedMessage id="freshdesk.description.closed"
                                       defaultMessage="Here you can <b>read</b> your <b>closed</b> tickets."
                                       values={{ b: (chunks) => <strong>{chunks}</strong> }}
        />}
        freshdeskTickets={closedTicketsList}
        classes={classes}
        closed
      />}
      
      <TicketList
        tickets={closedTicketsList}
        handleOpenTickets={handleOpenTickets}
        openTickets={openTickets}
        classes={classes}
        setIsSnackbarOpen={setIsSnackbarOpen}
      />
      
      {isSnackbarOpen && <CommonSnackbar
        open={isSnackbarOpen}
        title={<FormattedMessage id="message" defaultMessage="Message" />}
        message={<FormattedMessage id="reply.send" defaultMessage="Reply send!" />}
        timeframe={3000}
        onExtraClose={() => setIsSnackbarOpen(false)}
      />}
    </div>
  );
};

FreshdeskPage.propTypes = {
  freshdeskTickets: PropTypes.array,
  conversations: PropTypes.array,
  fetchTickets: PropTypes.func,
  fetchContact: PropTypes.func,
  fetchConversationById: PropTypes.func,
  user: PropTypes.object,
};

const decorate = compose(
  connect(store => ({
      user: store.user.profile,
      freshdeskTickets: store.freshdesk.freshdeskTickets,
      conversations: store.freshdesk.conversations,
    }),
    dispatch => bindActionCreators({
      fetchTickets,
      fetchContact,
      fetchConversationById,
    }, dispatch),
  ),
);

export default decorate(FreshdeskPage);
